/**
 * Campaign Wizard draft persistence — Patch 34.2.2g + Patch 37.4.39.
 *
 * Before Patch 34.2.2g, ``CampaignCommanderWizard`` used pure
 * ``useState`` for both ``step`` and ``answers``. Any unmount — a
 * refresh, a browser Back, a workspace switch, a token expiry — wiped
 * the entire in-progress campaign and dropped the founder at
 * Question 1.
 *
 * This hook stores the draft in workspace-scoped + user-scoped
 * localStorage so:
 *   * refresh restores the draft;
 *   * browser Back → forward restores the draft;
 *   * workspace switch never leaks another workspace's draft;
 *   * successful campaign creation clears the draft explicitly.
 *
 * Patch 37.4.39 · edit-mode isolation.
 *   When the wizard is opened with ``?resume={blueprint_id}`` (Back
 *   from Strategy Review), the draft key is scoped to that specific
 *   blueprint so an in-flight create-draft and an edit-resume can
 *   coexist without clobbering each other.
 */
import { useCallback, useEffect, useState } from "react";

import type { CommanderAnswers } from "../types";
import { useAuthStore } from "@/entities/auth/store";
import { useCurrentWorkspace } from "@/entities/workspace";


export interface CampaignWizardDraft {
  step: number;
  answers: Partial<CommanderAnswers>;
  updatedAt: string;
  /** Schema version — bump when the shape changes. */
  version: 1;
}

const DRAFT_VERSION = 1;
const EMPTY_DRAFT: CampaignWizardDraft = {
  step: 0,
  answers: { channels: [] },
  updatedAt: new Date(0).toISOString(),
  version: DRAFT_VERSION,
};

function draftKey(workspaceId: string, userId: string, scope: string): string {
  return `cz.wizard.draft.v${DRAFT_VERSION}.${workspaceId}.${userId}.${scope}`;
}

function readDraft(key: string): CampaignWizardDraft {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return { ...EMPTY_DRAFT };
    const parsed = JSON.parse(raw) as CampaignWizardDraft;
    if (parsed.version !== DRAFT_VERSION) return { ...EMPTY_DRAFT };
    return parsed;
  } catch {
    return { ...EMPTY_DRAFT };
  }
}

function writeDraft(key: string, draft: CampaignWizardDraft): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    /* localStorage may be full / disabled */
  }
}

/**
 * @param resumeBlueprintId  When provided, isolates this wizard
 *   session's localStorage from the "new campaign" draft so an edit
 *   resume never clobbers a create draft (Patch 37.4.39).
 */
export function useCampaignWizardDraft(resumeBlueprintId: string | null = null) {
  const ws = useCurrentWorkspace();
  const userId = useAuthStore((s) => s.user?.id ?? null);
  const scope = resumeBlueprintId ? `edit.${resumeBlueprintId}` : "new";
  const key = ws && userId ? draftKey(ws.id, userId, scope) : null;

  // Hydrate lazily so we never leak another workspace's draft when the
  // workspace changes mid-lifecycle.
  const [draft, setDraft] = useState<CampaignWizardDraft>(() =>
    key ? readDraft(key) : { ...EMPTY_DRAFT },
  );

  // When the workspace or user id or scope changes, re-hydrate — this
  // is what guarantees workspace + edit-mode isolation.
  useEffect(() => {
    if (!key) return;
    setDraft(readDraft(key));
  }, [key]);

  const persist = useCallback(
    (next: Pick<CampaignWizardDraft, "step" | "answers">) => {
      const stamped: CampaignWizardDraft = {
        ...next,
        updatedAt: new Date().toISOString(),
        version: DRAFT_VERSION,
      };
      setDraft(stamped);
      if (key) writeDraft(key, stamped);
    },
    [key],
  );

  const clear = useCallback(() => {
    setDraft({ ...EMPTY_DRAFT });
    if (key) {
      try {
        window.localStorage.removeItem(key);
      } catch {
        /* ignore */
      }
    }
  }, [key]);

  const hydrateFromBlueprint = useCallback(
    (answers: CommanderAnswers) => {
      const stamped: CampaignWizardDraft = {
        step: 5,               // land on Channels — every prior answer known
        answers,
        updatedAt: new Date().toISOString(),
        version: DRAFT_VERSION,
      };
      setDraft(stamped);
      if (key) writeDraft(key, stamped);
    },
    [key],
  );

  return {
    draft,
    persist,
    clear,
    hydrateFromBlueprint,
    hasDraft: draft.updatedAt !== EMPTY_DRAFT.updatedAt,
  };
}
